import type { Layout } from '../../data/types';
import type { Vec2 } from '../../geo/types';
import { centroid, pointInRing } from '../../geo/polygon';
import type { BakedCrowd } from './crowd/crowdPlan';
import { restoreWalkNet } from './crowd/walkNet';

export const CHILDREN_PER_GROUND = 5;

/*
 * Each neighbourhood runner keeps a distinct pace and starting distance, so a
 * group does not become one synchronized line of identical children.
 */
export function childDistanceAt(
  seconds: number,
  ground: number,
  child: number,
  length: number
): number {
  const speed = 2.2 + (child % 3) * 0.17;
  return seconds * speed + (child * length) / CHILDREN_PER_GROUND + ground * 3;
}

export interface ActivityGround {
  readonly name: string;
  readonly feeding?: { readonly citizen: Vec2; readonly food: Vec2; readonly heading: number };
  readonly musician: Vec2;
  readonly chase: readonly Vec2[];
  readonly pigeons: readonly Vec2[];
}

/** Use the crowd's tested ground and connected paths, never a straight cut through buildings. */
export function planActivityGround(layout: Layout, crowd: BakedCrowd): ActivityGround[] {
  const net = restoreWalkNet(crowd.net);
  const grounds: ActivityGround[] = [];
  for (const square of layout.squares) {
    const nodes = Array.from({ length: net.count }, (_, i) => i).filter(
      (i) => net.half[i]! >= 1.3 && pointInRing([net.x[i]!, net.y[i]!], square.polygon)
    );
    if (nodes.length < 3) continue;
    const point = (i: number): Vec2 => [net.x[i]!, net.y[i]!];
    const middle = centroid(square.polygon);
    const centre = [...nodes].sort(
      (i, j) =>
        Math.hypot(net.x[i]! - middle[0], net.y[i]! - middle[1]) -
        Math.hypot(net.x[j]! - middle[0], net.y[j]! - middle[1])
    )[0]!;
    const neighbour = net.to[net.start[centre]!]!;
    const heading = Math.atan2(
      net.y[neighbour]! - net.y[centre]!,
      net.x[neighbour]! - net.x[centre]!
    );
    const food: Vec2 = [
      net.x[centre]! + Math.cos(heading) * 1.8,
      net.y[centre]! + Math.sin(heading) * 1.8,
    ];
    const dam = /^dam$/i.test(square.name);
    const feeding = { citizen: point(centre), food, heading };
    const near = nodes.filter(
      (i) => Math.hypot(net.x[i]! - food[0], net.y[i]! - food[1]) < 8 && net.half[i]! >= 1.8
    );
    grounds.push({
      name: square.name,
      ...(dam ? { feeding } : {}),
      musician: point(nodes[0]!),
      chase: [],
      pigeons:
        dam && near.length
          ? Array.from({ length: 24 }, (_, k): Vec2 => {
              const node = near[k % near.length]!;
              const angle = k * 2.399;
              return [net.x[node]! + Math.cos(angle) * 0.55, net.y[node]! + Math.sin(angle) * 0.55];
            })
          : [],
    });
    if (grounds.length >= 6) break;
  }
  return grounds;
}
